import {
  Data,
  StepperFormMobile as SFM,
} from "@gromia/components/input/StepperFormMobile";
import { Textfield } from "@gromia/components/input/Textfield";
import * as f from "@gromia/formTypes";
import cx from "classnames";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as t from "io-ts";
import * as React from "react";
// import { Checkbox } from '@gromia/components/input/Checkbox';
import { createLead } from "@gromia/components/landing/LeadFormNoCity";
import * as s from "@gromia/components/theme/t.module.css";
import { Svg0059ErrorWarningDangerStop } from "@gromia/picons/0059ErrorWarningDangerStop";
import strings from "@gromia/strings/valuationStepper";
import { isEnabled, useFeatures } from "@gromia/utils/useFeatures";
import { useParams } from "@gromia/utils/useParams";
import { useString } from "@gromia/utils/useString";
import { humanReadableValuationData } from "./StepperDescription";

export const key = "valuation";

function useFormState() {
  return SFM.useFormState({
    key,
    defaultOpen: false,
    // defaultOpen: process.env.NODE_ENV === 'development',
  });
}

const Contact1Form = f.form({
  firstName: f.string,
  lastName: f.string,
  email: f.email,
  phoneNumber: f.string,
});

type Contact1FormT = t.TypeOf<typeof Contact1Form>;

export function Stepper(props: {
  state: ReturnType<typeof useFormState>;
  inline?: boolean;
}) {
  const features = useFeatures();
  const p = useParams();
  const refslug = p.parse(p.params.ref, undefined);
  const owner = p.parse(p.params.owner, undefined);
  const { tx } = useString("valuationStepper", strings);
  const floorx = ["1", "2", "3", "4", "5", "+6", "S", "T"];
  const floorsx = ["1", "2", "3", "4", "5", "+6"];

  function disabledFloorsList(data: Data): string[] {
    return pipe(
      data.floor,
      t.string.decode,
      O.fromEither,
      O.map(x => floorx.indexOf(x)),
      O.map(idx =>
        idx >= floorsx.length ? [] : floorsx.filter((_f, i) => i < idx)
      ),
      O.getOrElse(() => floorx),
    );
  }

  return (
    <SFM
      storeKey="valuation"
      thankYouPagePath={(data: Data) => {
        const contact1 = data.contact1 as Contact1FormT;
        return isEnabled(features.stepper.useOtpPhoneVerification)
          ? `/valuta/phone/?id=${data.id}&p=${contact1.phoneNumber}&em=${contact1.email}`
          : `/valuta/ty?em=${contact1.email}`;
      }}
      state={props.state}
      title={tx("title")}
      statics={{
        localitynotsupported: (
          <SFM.Message
            src={require("./i/address.svg").default}
            title={tx("localitynotsupportedTitle")}
            text={tx("localitynotsupportedText")}
          />
        ),
        timeframenotsupported: (
          <SFM.Message
            icn={Svg0059ErrorWarningDangerStop}
            title={tx("timeframenotsupportedTitle")}
            text={tx("timeframenotsupportedText")}
          />
        ),
      }}
      inline={props.inline}
      onSubmit={(data: Data) => {
        const contact1 = data.contact1 as Contact1FormT;
        const valuationDataHumanReadable = humanReadableValuationData(data);

        return createLead(
          {
            interest: "sell",
            name: contact1.firstName,
            surname: contact1.lastName,
            email: contact1.email,
            phone: contact1.phoneNumber,
            tos: true,
            refslug,
            owner,
            valuationData: JSON.stringify(data),
            valuationDataHumanReadable,
            tags: ["VALUTAZIONE"],
          },
          "valuation2",
        );
      }}
    >
      <SFM.Address
        name="location"
        addressLabel={tx("addressLabel")}
        streetNumberLabel={tx("streetNumberLabel")}
        title={tx("addressTitle")}
      />
      <SFM.ChoiceI
        title={tx("type1Title")}
        name="type"
        label={x => tx("type1Label", { variables: { v: x.v } })}
        buttons={[
          {
            v: "apartment",
            src: require("./i/01-appartamento-96px.svg").default,
          },
          { v: "house", src: require("./i/01-casa-96px.svg").default },
        ]}
      />
      <SFM.ChoiceI
        title={tx("condition")}
        name="condition"
        label={x => tx("conditionLabel", { variables: { v: x.v } })}
        buttons={[
          { v: "new", src: require("./i/14-4-lusso.svg").default },
          { v: "excellent", src: require("./i/14-3-di-pregio.svg").default },
          { v: "good", src: require("./i/14-2-standard.svg").default },
          {
            v: "to_be_renovated",
            src: require("./i/14-1-economiche.svg").default,
          },
        ]}
      />
      <SFM.Combo>
        <SFM.Choice
          title={tx("roomsTitle")}
          text={tx("roomsText")}
          name="rooms"
          label={v => tx("rooms", { variables: { v } })}
          buttons={["1", "2", "3", "4", "+5"]}
        />
        <SFM.Choice
          title={tx("bathroomsTitle")}
          text={null}
          name="bathrooms"
          label={v => tx("bathrooms", { variables: { v } })}
          buttons={["1", "2", "+3"]}
        />
      </SFM.Combo>
      <SFM.Combo>
        <SFM.Choice
          title={tx("floorTitle")}
          name="floor"
          label={v => tx("floor", { variables: { v } })}
          buttons={floorx}
          map={(data: Data) => {
            const disabled = disabledFloorsList(data);
            const copy = { ...data };
            delete copy["floors"];
            return disabled.includes(data.floors as string) ? copy : data;
          }}
        />
        <SFM.Choice
          title={tx("floorsTitle")}
          name="floors"
          label={v => tx("floors", { variables: { v } })}
          buttons={floorsx}
          disabled={disabledFloorsList}
        />
      </SFM.Combo>
      <SFM.Numeric
        title={tx("areaTitle")}
        label={<span />}
        name="area"
        min={0}
        max={300}
      />
      <SFM.Conditional filter={data => data.type === "house"}>
        <SFM.Numeric
          title={tx("areaGarden")}
          label={<span />}
          name="areaGarden"
          min={0}
          max={2000}
        />
      </SFM.Conditional>
      <SFM.MultiChoiceI
        title={tx("externalSpacesTitle")}
        name="externalSpaces"
        label={x => tx("externalSpaces", { variables: { v: x.v } })}
        buttons={[
          { v: "balcony", src: require("./i/12-balcone.svg").default },
          { v: "garage", src: require("./i/12-garage.svg").default },
          { v: "garden", src: require("./i/12-giardino.svg").default },
          { v: "parking", src: require("./i/12-posto-auto.svg").default },
          { v: "terrace", src: require("./i/12-terrazzo.svg").default },
        ]}
      />
      <SFM.ChoiceI
        title={tx("timeframeTitle")}
        name="timeframe"
        label={x => tx("timeframe", { variables: { v: x.v } })}
        buttons={[
          { v: "3", src: require("./i/15-vende-3-mesi.svg").default },
          { v: "6", src: require("./i/15-vende-6-mesi.svg").default },
          { v: "12", src: require("./i/15-vende-12-mesi.svg").default },
          { v: "dontknow", src: require("./i/15-tempo-non-so.svg").default },
        ]}
        // wayouts={{ dontknow: 'timeframenotsupported' }}
      />
      <SFM.Form
        title={tx("leadTitle")}
        text={tx("leadText")}
        name="contact1"
        decoder={() => Contact1Form}
      >
        {form => (
          <React.Fragment>
            <div className={cx(s.row)}>
              <div className={cx(s.col, s.col12, s.colSm6)}>
                <Textfield
                  type="text"
                  name="firstName"
                  label={tx("firstName")}
                  {...form.props("firstName")}
                />
              </div>
              <div className={cx(s.col, s.col12, s.colSm6)}>
                <Textfield
                  type="text"
                  name="lastName"
                  label={tx("lastName")}
                  {...form.props("lastName")}
                />
              </div>
            </div>
            <div className={cx(s.row)}>
              <div className={cx(s.col, s.col12, s.colLg6)}>
                <Textfield
                  type="text"
                  name="email"
                  label={tx("email")}
                  {
                    /*hint={tx('emailHint')}*/
                    ...form.props("email")
                  }
                />
              </div>
              <div className={cx(s.col, s.col12, s.colLg6)}>
                <Textfield
                  type="tel"
                  name="phoneNumber"
                  label={tx("phoneNumber")}
                  prependNode="+39"
                  {...form.props("phoneNumber")}
                />
              </div>
              <div className={cx(s.col, s.col12)}>
                <span className={cx(s.fontSizeSmall2)}>
                  {tx("phoneNumberSpam")}
                </span>
              </div>
            </div>
            <div className={cx(s.fontSizeSmall2, s.mt4)}>{tx("tos")}</div>
          </React.Fragment>
        )}
      </SFM.Form>
    </SFM>
  );
}

Stepper.useFormState = useFormState;
